import cloneDeep from "lodash/cloneDeep";
import merge from "lodash/merge";
import defaultConfig from "./defaultConfig.json";
import builtinTemplate1Config from "./builtinTemplate1.json";
import builtinTemplate2Config from "./builtinTemplate2.json";

export const BUILTIN_TEMPLATE_PREFIX = "__builtin:";

export const DEFAULT_BUILTIN_TEMPLATE_ID = `${BUILTIN_TEMPLATE_PREFIX}template-1`;
export const BUILTIN_TEMPLATE_2_ID = `${BUILTIN_TEMPLATE_PREFIX}template-2`;

export const BUILTIN_TEMPLATES = [
  {
    _id: DEFAULT_BUILTIN_TEMPLATE_ID,
    name: "محضر مجلس القسم",
    type: "MP",
    status: "active",
    isBuiltin: true,
    config: builtinTemplate1Config,
  },
  {
    _id: BUILTIN_TEMPLATE_2_ID,
    name: "محضر في التلاميذ المرتقين إلى السنة السابعة من التعليم الأساسي",
    type: "MS",
    status: "active",
    isBuiltin: true,
    config: builtinTemplate2Config,
  },
];

export const isBuiltinTemplateId = (id) =>
  String(id || "").startsWith(BUILTIN_TEMPLATE_PREFIX);

export const getBuiltinTemplate = (id) =>
  BUILTIN_TEMPLATES.find((t) => t._id === id);

export const getDefaultBuiltinTemplate = () =>
  getBuiltinTemplate(DEFAULT_BUILTIN_TEMPLATE_ID) || BUILTIN_TEMPLATES[0];

export const resolveTemplateConfig = (template) =>
  normalizeConseilConfig(template?.config ?? template);

const mergeSection = (defaults, value) =>
  merge(cloneDeep(defaults), value && typeof value === "object" ? value : {});

export const normalizeConseilConfig = (config) => {
  const merged = merge(cloneDeep(defaultConfig), config || {});
  merged.headers = mergeSection(defaultConfig.headers, merged.headers);
  merged.table = mergeSection(defaultConfig.table, merged.table);
  merged.table.preParagraph = mergeSection(
    defaultConfig.table.preParagraph,
    merged.table.preParagraph
  );
  merged.table.postParagraph = mergeSection(
    defaultConfig.table.postParagraph,
    merged.table.postParagraph
  );
  merged.footer = mergeSection(defaultConfig.footer, merged.footer);
  merged.footer.exceptionalPass = mergeSection(
    defaultConfig.footer.exceptionalPass,
    merged.footer.exceptionalPass
  );
  merged.footer.agePass = mergeSection(
    defaultConfig.footer.agePass,
    merged.footer.agePass
  );
  merged.footer.fail = mergeSection(
    defaultConfig.footer.fail,
    merged.footer.fail
  );
  merged.lastPage = mergeSection(defaultConfig.lastPage, merged.lastPage);
  merged.lastPage.managerAndTeacher = mergeSection(
    defaultConfig.lastPage.managerAndTeacher,
    merged.lastPage.managerAndTeacher
  );
  // lodash `merge` combines arrays by index, so a shorter saved `columnsOrder`
  // over the longer default leaves stale trailing entries -> duplicate columns
  // (e.g. moy/decision twice). Take the source array verbatim (deduped) instead
  // of the index-merged result; fall back to the default order when absent.
  const sourceOrder = config?.table?.columnsOrder;
  merged.table.columnsOrder = [
    ...new Set(
      Array.isArray(sourceOrder) && sourceOrder.length
        ? sourceOrder
        : defaultConfig.table.columnsOrder
    ),
  ];
  return merged;
};

export const mergeTemplatesForPicker = (savedTemplates = []) => {
  const dbTemplates = dedupeSavedTemplatesByLevel(savedTemplates).filter(
    (t) => t && !isBuiltinTemplateId(t._id)
  );
  return [...BUILTIN_TEMPLATES, ...dbTemplates];
};

/** One level-wide template per level+type+schoolarYear (keeps most recently updated). */
export const dedupeSavedTemplatesByLevel = (savedTemplates = []) => {
  const byKey = new Map();
  const classRoomTemplates = [];

  for (const t of savedTemplates || []) {
    if (!t || isBuiltinTemplateId(t._id)) continue;
    if (t.classRoom) {
      classRoomTemplates.push(t);
      continue;
    }
    const key = `${t.type}|${Number(t.level)}|${String(
      t.schoolarYear?._id || t.schoolarYear
    )}`;
    const prev = byKey.get(key);
    if (!prev || String(t.updatedAt || "") > String(prev.updatedAt || "")) {
      byKey.set(key, t);
    }
  }

  return [...byKey.values(), ...classRoomTemplates];
};

export const findTemplateById = (templates, id) =>
  (templates || []).find((t) => t._id === id);

export const findAssignedTemplate = (
  templates,
  { level, type, schoolarYear }
) =>
  (templates || []).find(
    (t) =>
      !t.isBuiltin &&
      !t.classRoom &&
      Number(t.level) === Number(level) &&
      t.type === type &&
      String(t.schoolarYear?._id || t.schoolarYear) === String(schoolarYear)
  );

export const resolvePrintConfig = (savedConfig) => {
  if (savedConfig) {
    return normalizeConseilConfig(savedConfig);
  }
  return resolveTemplateConfig(getDefaultBuiltinTemplate());
};

/** Load the level-wide saved ConseilClass config (if any). */
export async function fetchSavedConseilConfig(
  apiClient,
  { level, type, schoolarYear }
) {
  const { data } = await apiClient.post("/bulletin/v2/conseil_class/filter", {
    level: Number(level),
    type,
    schoolarYear,
  });
  const record = Array.isArray(data) ? data[0] : null;
  return record?.config || null;
}

/**
 * Student marks print: use the saved per-level template when it exists,
 * otherwise fall back to the built-in default.
 */
export async function resolveStudentMarksPrintConfig(
  apiClient,
  { level, type, schoolarYear }
) {
  try {
    const saved = await fetchSavedConseilConfig(apiClient, {
      level,
      type,
      schoolarYear,
    });
    return resolvePrintConfig(saved);
  } catch (error) {
    console.warn("ConseilClass: could not load saved template, using built-in", error);
    return resolvePrintConfig(null);
  }
}
